import React from "react";
import Select, { StylesConfig, SingleValue } from "react-select";
import { useField } from "formik";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import placeholderImage from "../../assets/images/landscape-placeholder.svg";
import { withErrorMessage } from "../withErrorMessage";
import { Picture } from "../Picture";
import { BrigadeOption } from './BrigadeSelect.types';
import { publicService } from '../../services/publicService';

type Props = {
  name: string;
  isDisabled?: boolean;
};

export const BrigadeSelect: React.FC<Props> = withErrorMessage(
  ({ name, isDisabled }) => {
    const { t } = useTranslation('victim', { keyPrefix: 'brigadeSelect' });
    const [, meta, helpers] = useField<BrigadeOption | null>(name);

    const { data: brigades, isLoading } = useQuery({
      queryKey: ["brigades"],
      queryFn: publicService.getBrigades,
      select: (data) => [
        { value: 0, label: t('emptyValue'), image: placeholderImage },
        ...data.map(d => ({ value: d.id, label: d.name, image: d.image })),
      ],
    });

    const { value } = meta;
    const { setValue } = helpers;

    const customSelectStyles: StylesConfig<BrigadeOption> = {
      control: (provided) => ({
        ...provided,
        borderRadius: "var(--bs-border-radius-pill) !important",
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: 4,
        borderRadius: "var(--bs-border-radius-pill) !important",
        fontSize: "calc(1.375rem)",
        backgroundColor: "white",
      }),
      option: (provided) => ({
        ...provided,
        cursor: "pointer",
        fontSize: 16,
      }),
      indicatorSeparator: (provided) => ({
        ...provided,
        display: "none",
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: "white",
      }),
    };

    const handleSelectChange = (newValue: SingleValue<BrigadeOption>) => {
      setValue(newValue?.value === 0 ? null : newValue);
    };

    return (
      <Select
        isDisabled={isDisabled}
        isMulti={false}
        value={value}
        onChange={handleSelectChange}
        options={brigades}
        maxMenuHeight={500}
        isLoading={isLoading}
        isSearchable
        loadingMessage={() => <span>{t("loading")}</span>}
        noOptionsMessage={() => <span>{t("noSuchBrigades")}</span>}
        aria-errormessage={t("errorMessage")}
        formatOptionLabel={(brigade) => (
          <div className="d-flex ps-2 align-items-center">
            {brigade.value === 0 ? (
              <img
                src={brigade.image}
                alt={brigade.label}
                className="table-image rounded-circle me-3"
              />
            ) : (
              <Picture
                src={brigade.image}
                alt={brigade.label}
                className="table-image rounded-circle me-3"
              />
            )}
            <span>{brigade.label} </span>
          </div>
        )}
        placeholder={
          <div className="d-flex justify-content-start ps-1 fs-4 text-secondary">
            {t("placeholder")}
          </div>
        }
        filterOption={(option, inputValue) =>
          option.label.toLowerCase().includes(inputValue.trim().toLowerCase())
        }
        styles={customSelectStyles}
      />
    );
  },
);
